import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import useDebounce from '../../hooks/useDebounce';
import SteamAvatar from './SteamAvatar';
import { NEWS_CATEGORY_MAP } from '../news/newsConstants';
import './CommandPalette.css';

// Static destinations always reachable from the palette. Hybrid-only routes
// (queue/create/admin) are intentionally omitted — the palette is a public
// quick-nav, not a permission surface. Labels reuse the nav.* keys; the
// translated string is resolved at search/render time (see results memo).
const PAGES = [
  { labelKey: 'nav.home', path: '/', icon: '🏠' },
  { labelKey: 'nav.news', path: '/news', icon: '📰' },
  { labelKey: 'nav.leaderboard', path: '/stats/leaderboard', icon: '🏆' },
  { labelKey: 'nav.players', path: '/stats/players', icon: '👥' },
  { labelKey: 'nav.matches', path: '/stats/matches', icon: '📋' },
  { labelKey: 'nav.maps', path: '/stats/maps', icon: '🗺️' },
  { labelKey: 'nav.medals', path: '/stats/medaljer', icon: '🎖️' },
  { labelKey: 'nav.tournaments', path: '/tournaments', icon: '🥇' },
  { labelKey: 'nav.leagues', path: '/leagues', icon: '🏆' },
  { labelKey: 'nav.highlights', path: '/highlights', icon: '🎬' },
  { labelKey: 'nav.live', path: '/live', icon: '🔴' },
];

/**
 * Global command palette / quick-search overlay (Fas 11A).
 *
 * Opens on `Ctrl/⌘ + K` (toggle) or on a `open-cmdk` window event dispatched by
 * the header search button, so both keyboard and touch users have an entry
 * point. Searches four sources client-side: the static page list (above), the
 * player roster (lazy-loaded from `/api/players` the first time the palette
 * opens), published news (lazy-loaded from `/api/news`, matched on title /
 * excerpt / category) and — when the query is purely numeric — a direct "go to
 * match #N" shortcut. Results are a single flat list driving arrow-key navigation; Enter
 * follows the active row, Esc / backdrop-click closes. Manages its own open
 * state, so mounting it once in `App` (inside the router) is all that is needed.
 *
 * @returns {JSX.Element|null} The overlay when open, otherwise `null`.
 */
const CommandPalette = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [players, setPlayers] = useState([]);
  const [news, setNews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debouncedQuery = useDebounce(query, 120);

  // Global hotkey (⌘K / Ctrl+K) + header-button event open the palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-cmdk', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-cmdk', onOpenEvent);
    };
  }, []);

  // Lazy-load the roster once, and reset/focus each time the palette opens.
  useEffect(() => {
    if (!open) return;
    if (!loaded) {
      fetch(`${API_URL}/api/players`)
        .then((r) => r.json())
        .then((d) => { setPlayers(d.players || []); setLoaded(true); })
        .catch(() => setLoaded(true));
      // Published news for the palette (title/excerpt/category search). The list
      // endpoint omits the markdown body, so this stays lightweight.
      fetch(`${API_URL}/api/news`)
        .then((r) => r.json())
        .then((d) => setNews(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
    setQuery('');
    setActiveIndex(0);
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open, loaded]);

  const results = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    const pageHits = PAGES
      .map((p) => ({ type: 'page', key: p.path, path: p.path, icon: p.icon, label: t(p.labelKey) }))
      .filter((p) => !q || p.label.toLowerCase().includes(q));

    let playerHits = [];
    if (q) {
      playerHits = players
        .filter((p) => (p.name || '').toLowerCase().includes(q))
        .sort((a, b) => {
          const aStart = (a.name || '').toLowerCase().startsWith(q) ? 0 : 1;
          const bStart = (b.name || '').toLowerCase().startsWith(q) ? 0 : 1;
          if (aStart !== bStart) return aStart - bStart;
          return (a.name || '').localeCompare(b.name || '', 'sv');
        })
        .slice(0, 8)
        .map((p) => ({ type: 'player', key: `p-${p.steamid64}`, label: p.name, steamid64: p.steamid64 }));
    }

    let newsHits = [];
    if (q) {
      newsHits = news
        .filter((n) => {
          const cat = NEWS_CATEGORY_MAP[n.category];
          const hay = `${n.title || ''} ${n.excerpt || ''} ${cat ? t(cat.labelKey) : ''}`.toLowerCase();
          return hay.includes(q);
        })
        .slice(0, 6)
        .map((n) => ({
          type: 'news',
          key: `n-${n.id}`,
          label: n.title,
          slug: n.slug,
          postType: n.post_type,
          externalUrl: n.external_url,
          icon: NEWS_CATEGORY_MAP[n.category]?.emoji || '📰',
        }));
    }

    const matchHits = /^\d+$/.test(q)
      ? [{ type: 'match', key: `m-${q}`, label: t('palette.matchHash', { n: q }), matchid: q, icon: '📄' }]
      : [];

    return [...pageHits, ...playerHits, ...newsHits, ...matchHits];
  }, [debouncedQuery, players, news, t]);

  // Keep the active row in range as results change.
  useEffect(() => { setActiveIndex(0); }, [debouncedQuery]);

  const go = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    if (item.type === 'page') navigate(item.path);
    else if (item.type === 'player') navigate(`/stats/players/${item.steamid64}`);
    else if (item.type === 'match') navigate(`/stats/matches/${item.matchid}`);
    else if (item.type === 'news') {
      // External posts link out; on-site posts open their detail page.
      if (item.postType === 'external' && item.externalUrl) {
        window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(`/news/${item.slug}`);
      }
    }
  }, [navigate]);

  const onInputKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[activeIndex]);
    }
  };

  if (!open) return null;

  return (
    <div className="cmdk-overlay" onMouseDown={() => setOpen(false)}>
      <div className="cmdk-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cmdk-input-row">
          <span className="cmdk-input-icon" aria-hidden="true">🔎</span>
          <input
            ref={inputRef}
            className="cmdk-input"
            type="text"
            placeholder={t('palette.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            aria-label={t('nav.search')}
          />
          <kbd className="cmdk-esc">Esc</kbd>
        </div>
        <div className="cmdk-results">
          {results.length === 0 ? (
            <div className="cmdk-empty">{t('palette.noResults')}</div>
          ) : (
            results.map((item, i) => (
              <button
                key={item.key}
                type="button"
                className={`cmdk-item ${i === activeIndex ? 'active' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => go(item)}
              >
                {item.type === 'player' ? (
                  <SteamAvatar steamid64={item.steamid64} name={item.label} size="small" />
                ) : (
                  <span className="cmdk-item-icon" aria-hidden="true">{item.icon || '➡️'}</span>
                )}
                <span className="cmdk-item-label">{item.label}</span>
                <span className="cmdk-item-type">
                  {item.type === 'player' ? t('palette.typePlayer')
                    : item.type === 'match' ? t('palette.typeMatch')
                    : item.type === 'news' ? t('palette.typeNews')
                    : t('palette.typePage')}
                </span>
              </button>
            ))
          )}
        </div>
        <div className="cmdk-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> {t('palette.navHint')}</span>
          <span><kbd>↵</kbd> {t('palette.openHint')}</span>
          <span><kbd>Esc</kbd> {t('palette.closeHint')}</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
